import React, { useCallback, useMemo, useState } from 'react';
import { Button, Modal, Input, Typography, Empty, Table, Select, Tag, message } from 'antd';
import { EyeOutlined, PlusOutlined, FolderOutlined, PaperClipOutlined, LinkOutlined, FileTextOutlined } from '@ant-design/icons';
import PageShell from '../components/page/PageShell';
import PageHeader from '../components/page/PageHeader';
import PageState from '../components/page/PageState';
import DataCard from '../components/page/DataCard';
import { useApp } from '../context/AppContext';
import { pickScopingPersistPayload } from '../utils/pickScopingPersistPayload';
import { buildEvidenceVaultRows } from '../utils/evidenceVaultRows';
import { getEvidenceEditPermissions } from '../utils/evidenceAccess';

const { Text } = Typography;
const { TextArea } = Input;

const STATUS_OPTIONS = [
    { value: 'approved', label: 'Approved' },
    { value: 'not_approved', label: 'Not approved' },
];

/**
 * Evidence view — per-project evidence management with versioning.
 */
const EvidenceView = () => {
    const {
        user,
        activeProject,
        updateProjectProfile,
        saveScopingProfile,
        fetchProjectProfile,
        openBlobUrl,
    } = useApp();
    const [viewingId, setViewingId] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [newContent, setNewContent] = useState('');
    const [savingRowKey, setSavingRowKey] = useState(null);
    const [savingVersion, setSavingVersion] = useState(false);

    if (!activeProject) {
        return <PageState status="empty" message="No project selected" />;
    }

    const profile = activeProject.profile || {};
    const evidence = Array.isArray(profile.evidence) ? profile.evidence : [];
    const evidenceFileArtifacts = Array.isArray(profile.evidenceFileArtifacts)
        ? profile.evidenceFileArtifacts
        : [];

    const tableRows = useMemo(() => buildEvidenceVaultRows(profile), [profile]);

    // See utils/evidenceAccess.js for why this is id-based (not name-string) and uses
    // dbUserId (not the msal-{email} placeholder) with lowercase role literals — the exact
    // bug fixed at 09466d7, see .claude/lessons/frontend-role-comparison-casing-and-placeholder-id.md.
    const { isLeadAdvisor, isAdvisor, canEdit } = getEvidenceEditPermissions(user, activeProject);

    const persistProfile = useCallback(
        async (newProfile, errMsg) => {
            updateProjectProfile(activeProject.id, newProfile);
            if (!saveScopingProfile) {
                message.warning('Changes are local only until you sign in.');
                return false;
            }
            const ok = await saveScopingProfile(activeProject.id, pickScopingPersistPayload(newProfile));
            if (!ok) {
                message.error(errMsg || 'Could not save to the server.');
                if (fetchProjectProfile) await fetchProjectProfile(activeProject.id);
                return false;
            }
            if (fetchProjectProfile) await fetchProjectProfile(activeProject.id);
            return true;
        },
        [activeProject.id, updateProjectProfile, saveScopingProfile, fetchProjectProfile],
    );

    const handleReviewStatusChange = useCallback(
        async (row, nextStatus) => {
            if (!row || !canEdit) return;
            const base = activeProject.profile || {};
            setSavingRowKey(row.rowKey);
            try {
                if (row.rowKind === 'file' && row.artifact) {
                    const id = row.artifact.id;
                    const nextArtifacts = evidenceFileArtifacts.map((a) =>
                        a.id === id ? { ...a, reviewStatus: nextStatus } : a,
                    );
                    const newProfile = { ...base, evidenceFileArtifacts: nextArtifacts };
                    await persistProfile(newProfile, 'Could not save evidence status.');
                } else if (row.rowKind === 'text' && row.evidenceItem) {
                    const id = row.evidenceItem.id;
                    const nextEvidence = evidence.map((e) =>
                        e.id === id ? { ...e, reviewStatus: nextStatus } : e,
                    );
                    const newProfile = { ...base, evidence: nextEvidence };
                    await persistProfile(newProfile, 'Could not save evidence status.');
                }
            } finally {
                setSavingRowKey(null);
            }
        },
        [activeProject.profile, canEdit, evidence, evidenceFileArtifacts, persistProfile],
    );

    const handleNewVersion = (evidenceId) => {
        const item = evidence.find((e) => e.id === evidenceId);
        if (!item) return;
        const latest = item.versions[item.versions.length - 1];
        setNewContent(latest.content);
        setEditingId(evidenceId);
    };

    const handleSaveVersion = async () => {
        if (!editingId) return;
        setSavingVersion(true);
        try {
            const base = activeProject.profile || {};
            const updated = evidence.map((e) =>
                e.id === editingId
                    ? {
                        ...e,
                        versions: [
                            ...e.versions,
                            { content: newContent, createdBy: user?.role || 'UNKNOWN', timestamp: Date.now() },
                        ],
                    }
                    : e,
            );
            const newProfile = { ...base, evidence: updated };
            const ok = await persistProfile(newProfile, 'Could not save new evidence version.');
            if (ok) {
                message.success('Version saved.');
                setEditingId(null);
            }
        } finally {
            setSavingVersion(false);
        }
    };

    const viewingItem = evidence.find((e) => e.id === viewingId);
    const viewingLatest = viewingItem ? viewingItem.versions[viewingItem.versions.length - 1] : null;

    const columns = useMemo(
        () => [
            {
                title: 'Type',
                key: 'type',
                width: 100,
                render: (_, row) =>
                    row.rowKind === 'file' ? (
                        <Tag icon={<PaperClipOutlined />} color="green">
                            File
                        </Tag>
                    ) : (
                        <Tag icon={<FileTextOutlined />} color="blue">
                            Text
                        </Tag>
                    ),
            },
            {
                title: 'Section name',
                dataIndex: 'sectionName',
                key: 'sectionName',
                ellipsis: true,
            },
            {
                title: 'Subtab name',
                dataIndex: 'subtabName',
                key: 'subtabName',
                ellipsis: true,
            },
            {
                title: 'Status',
                key: 'status',
                width: 200,
                render: (_, row) => {
                    if (canEdit) {
                        return (
                            <Select
                                size="small"
                                style={{ minWidth: 160 }}
                                value={row.reviewStatus}
                                options={STATUS_OPTIONS}
                                loading={savingRowKey === row.rowKey}
                                disabled={savingRowKey === row.rowKey}
                                onChange={(v) => handleReviewStatusChange(row, v)}
                            />
                        );
                    }
                    return (
                        <Tag color={row.reviewStatus === 'approved' ? 'success' : 'default'}>
                            {row.reviewStatus === 'approved' ? 'Approved' : 'Not approved'}
                        </Tag>
                    );
                },
            },
            {
                title: 'Date',
                dataIndex: 'dateStr',
                key: 'dateStr',
                width: 120,
            },
            {
                title: 'Time',
                dataIndex: 'timeStr',
                key: 'timeStr',
                width: 110,
            },
            {
                title: 'File name',
                key: 'fileName',
                ellipsis: true,
                render: (_, row) => {
                    if (row.rowKind === 'file') {
                        const raw = row.artifact?.blobUrl || row.artifact?.url;
                        return (
                            <Button
                                type="link"
                                size="small"
                                style={{ padding: 0, fontWeight: 700, height: 'auto' }}
                                icon={<LinkOutlined style={{ fontSize: 11 }} />}
                                onClick={() => {
                                    // openBlobUrl resolves false (never a raw/unsigned URL) when
                                    // re-signing fails or throws — surface that, don't fail silent.
                                    void Promise.resolve(openBlobUrl?.(raw)).then((ok) => {
                                        if (ok === false) {
                                            message.error('A secure link could not be generated. Please retry.');
                                        }
                                    });
                                }}
                            >
                                {row.fileDisplayName}
                            </Button>
                        );
                    }
                    return (
                        <Button
                            type="link"
                            size="small"
                            style={{ padding: 0, fontWeight: 700, height: 'auto' }}
                            icon={<EyeOutlined style={{ fontSize: 11 }} />}
                            onClick={() => setViewingId(row.evidenceItem.id)}
                        >
                            {row.fileDisplayName}
                        </Button>
                    );
                },
            },
            {
                title: 'Actions',
                key: 'actions',
                width: 140,
                render: (_, row) =>
                    row.rowKind === 'text' ? (
                        <Button
                            type="primary"
                            size="small"
                            icon={<PlusOutlined />}
                            disabled={!canEdit}
                            onClick={() => handleNewVersion(row.evidenceItem.id)}
                        >
                            New version
                        </Button>
                    ) : (
                        <Text type="secondary">-</Text>
                    ),
            },
        ],
        [canEdit, handleReviewStatusChange, openBlobUrl, savingRowKey],
    );

    const hasAnyEvidence = tableRows.length > 0;

    return (
        <PageShell>
            <PageHeader title="Evidence Vault" subtitle={`Manage audit evidence for ${activeProject.name}`} />

            {!hasAnyEvidence ? (
                <DataCard style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Empty
                        image={<FolderOutlined style={{ fontSize: 48, color: 'var(--color-border-strong)' }} />}
                        description={
                            <div>
                                <Text style={{ fontSize: 14 }}>No evidence saved yet.</Text>
                                <br />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Evidence artifacts will appear here once submitted.
                                </Text>
                            </div>
                        }
                    />
                </DataCard>
            ) : (
                <DataCard
                    title={
                        <span style={{ fontWeight: 800, fontSize: 15 }}>
                            <PaperClipOutlined style={{ marginRight: 8, color: 'var(--color-ok-fg)' }} />
                            Evidence register
                        </span>
                    }
                >
                    <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 12 }}>
                        Uploaded files (Gap analysis, Control implementation) and documented text evidence. Files open in
                        a new tab using your organization&apos;s secure storage.
                    </Text>
                    <Table
                        size="small"
                        rowKey="rowKey"
                        columns={columns}
                        dataSource={tableRows}
                        pagination={tableRows.length > 12 ? { pageSize: 12 } : false}
                        scroll={{ x: 960 }}
                    />
                </DataCard>
            )}

            <Modal
                open={!!viewingId && !editingId}
                onCancel={() => setViewingId(null)}
                title="Evidence viewer"
                footer={null}
                width={640}
            >
                {viewingLatest && (
                    <pre
                        style={{
                            background: 'var(--color-bg-base)',
                            padding: 16,
                            fontSize: 13,
                            whiteSpace: 'pre-wrap',
                            border: '1px solid var(--color-border)',
                        }}
                    >
                        {viewingLatest.content}
                    </pre>
                )}
            </Modal>

            <Modal
                open={!!editingId}
                onCancel={() => setEditingId(null)}
                title="Add new version"
                onOk={handleSaveVersion}
                okText="Save version"
                confirmLoading={savingVersion}
                okButtonProps={{ style: { fontWeight: 700 } }}
            >
                <TextArea rows={12} value={newContent} onChange={(e) => setNewContent(e.target.value)} style={{ marginTop: 12 }} />
            </Modal>
        </PageShell>
    );
};

export default EvidenceView;
